import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "../../utils/toast";
import { Shield, Plus, Pencil, Trash2, Info } from "lucide-react";
import RoleModal from "./RoleModal";
import staticPermissionsData from "../../data/permissionsData";
import { API_URL } from "../../api/api";
import { T } from "../../components/em/tokens";

import { confirmDialog } from "../../components/em/confirm";
const RolePermission = () => {
  const [openModal, setOpenModal] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [rolesData, setRolesData] = useState([]);
  const [loading, setLoading] = useState(false);
  // Permission catalog comes from the backend (single source of truth); the
  // bundled static file is only a fallback if the request fails.
  const [permissionsData, setPermissionsData] = useState(staticPermissionsData);

  const baseURL = API_URL;

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseURL}/api/roles`);
      setRolesData(res.data || []);
    } catch (err) {
      console.error("Fetch roles error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCatalog = async () => {
    try {
      const res = await axios.get(`${baseURL}/api/roles/permissions/catalog`);
      if (Array.isArray(res.data) && res.data.length) setPermissionsData(res.data);
    } catch (err) {
      console.error("Fetch permission catalog error:", err);
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchCatalog();
  }, []);

  const handleCreate = () => {
    setEditRole(null);
    setOpenModal(true);
  };

  const handleEdit = async (roleId) => {
    try {
      const res = await axios.get(`${baseURL}/api/roles/${roleId}`);
      setEditRole(res.data);
      setOpenModal(true);
    } catch (err) {
      console.error("Fetch role details error:", err);
      toast.error("Failed to load role details");
    }
  };

  const handleSave = async () => {
    setOpenModal(false);
    setEditRole(null);
    fetchRoles();
  };

  const handleDelete = async ({ id: roleId, name: roleName, user_count }) => {
    // A role someone still holds can't be deleted (the server refuses it too) —
    // say so straight away rather than asking "are you sure?" for a delete
    // that is going to fail.
    const holders = Number(user_count) || 0;
    if (holders > 0) {
      toast.warning(`"${roleName}" is still assigned to ${holders === 1 ? "1 user" : `${holders} users`}`, {
        description: "Move them to another role from their employee profile first, then delete this role.",
        duration: 7000,
      });
      return;
    }
    const confirmed = await confirmDialog(
      `Are you sure you want to delete the role "${roleName}"?`
    );
    if (!confirmed) return;
    try {
      await axios.delete(`${baseURL}/api/roles/${roleId}`);
      toast.success("Role deleted successfully");
      fetchRoles();
    } catch (err) {
      console.error("Delete role error:", err);
      // 409 ROLE_IN_USE — someone was given the role after this list loaded.
      // The server's message names who still holds it.
      if (err?.response?.data?.code === "ROLE_IN_USE") {
        toast.warning(err.response.data.error, { duration: 8000 });
        fetchRoles();
        return;
      }
      toast.error(err?.response?.data?.error || "Failed to delete role");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{ width: 42, height: 42, borderRadius: 12, background: T.orange50, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Shield size={20} color={T.orange} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ font: `700 20px ${T.font}`, color: T.fg1, letterSpacing: "-0.02em" }}>
            Roles &amp; Access Management
          </div>
          <div style={{ font: `400 12px ${T.font}`, color: T.fg5 }}>
            Define system roles and their access permissions
          </div>
        </div>
        <button
          onClick={handleCreate}
          style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 18px", borderRadius: 10, border: 0, cursor: "pointer", background: T.gradient, color: "#fff", font: `700 13px ${T.font}`, boxShadow: T.shadowBrand }}
        >
          <Plus size={15} /> Create Role
        </button>
      </div>

      <RoleModal
        isOpen={openModal}
        onClose={() => {
          setOpenModal(false);
          setEditRole(null);
        }}
        roleData={editRole}
        permissionsData={permissionsData}
        onSave={handleSave}
      />

      {/* About card (blue) */}
      <div
        style={{
          display: "flex",
          gap: 14,
          padding: "18px 20px",
          borderRadius: 14,
          background: "linear-gradient(135deg,#eff6ff,#f0f9ff)",
          border: "1px solid #bfdbfe",
        }}
      >
        <div style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(43,127,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Info size={18} color={T.info} />
        </div>
        <div>
          <div style={{ font: `700 14px ${T.font}`, color: T.fg1, marginBottom: 4 }}>
            About Roles &amp; Permissions
          </div>
          <div style={{ font: `400 13px/1.6 ${T.font}`, color: "#1e3a5f" }}>
            Admin always has full access to every part of this application — its permissions are
            fixed and cannot be edited, so the organisation can never be locked out of its own
            administration. Every other role, <b>including HR</b>, gets access only from what you
            grant here. System role names are protected, but their permissions are yours to set, and
            you can create custom roles for specific access levels. All changes are applied
            immediately across the application.
          </div>
        </div>
      </div>

      {/* Roles grid */}
      {loading ? (
        <div style={{ padding: 40, textAlign: "center", font: `400 14px ${T.font}`, color: T.fg5 }}>
          Loading roles…
        </div>
      ) : rolesData.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", font: `400 14px ${T.font}`, color: T.fg5 }}>
          No roles found
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
          {rolesData.map((role) => (
            <div
              key={role.id}
              style={{ background: "var(--em-bg-card)", border: `1px solid ${T.border}`, borderRadius: 14, padding: 20, boxShadow: T.shadowSm, display: "flex", flexDirection: "column" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: T.orange50, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Shield size={17} color={T.orange} />
                </div>
                <div style={{ font: `700 16px ${T.font}`, color: T.fg1, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {role.name}
                </div>
                {role.color && (
                  <span style={{ width: 14, height: 14, borderRadius: 9999, background: role.color, flexShrink: 0, border: "1px solid rgba(0,0,0,0.1)" }} />
                )}
              </div>

              <div style={{ font: `400 13px/1.5 ${T.font}`, color: T.fg5, marginBottom: 12, minHeight: 38 }}>
                {role.description || "No description added"}
              </div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "3px 10px",
                    borderRadius: 9999,
                    background: T.orangeSoft,
                    color: T.orange,
                    font: `600 11px ${T.font}`,
                  }}
                >
                  {role.permission_count || 0} permissions assigned
                </div>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "3px 10px",
                    borderRadius: 9999,
                    background: "var(--em-bg-muted, #f1f5f9)",
                    color: T.fg5,
                    font: `600 11px ${T.font}`,
                  }}
                >
                  {Number(role.user_count) === 1 ? "1 user" : `${Number(role.user_count) || 0} users`}
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, marginTop: "auto" }}>
                <button
                  onClick={() => handleEdit(role.id)}
                  style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "9px 12px", borderRadius: 8, border: `1px solid ${T.border}`, background: "var(--em-bg-card)", color: T.fg1, font: `600 13px ${T.font}`, cursor: "pointer" }}
                >
                  <Pencil size={14} /> Edit
                </button>
                <button
                  onClick={() => handleDelete(role)}
                  style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "9px 12px", borderRadius: 8, border: "1px solid #fecaca", background: T.danger50, color: T.danger, font: `600 13px ${T.font}`, cursor: "pointer" }}
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RolePermission;
